import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "input", "required", "assigned"]
  static values = {
    projectId: Number,
    initialValue: Number,
    editable: { type: Boolean, default: true }
  }

  connect() {
    this.originalValue = this.initialValueValue
  }

  edit(event) {
    event.preventDefault()
    if (!this.editableValue) {
      return // Don't allow editing if not editable (e.g., historical sprint)
    }
    this.showInput()
  }

  showInput() {
    this.displayTarget.classList.add('hidden')
    this.inputTarget.classList.remove('hidden')
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  hideInput() {
    this.inputTarget.classList.add('hidden')
    this.displayTarget.classList.remove('hidden')
  }

  cancel(event) {
    event.preventDefault()
    this.inputTarget.value = this.originalValue
    this.hideInput()
  }

  async save(event) {
    // Don't submit if Tab was pressed (let it move focus naturally)
    if (event.type === 'keydown' && event.key === 'Tab') {
      event.preventDefault()
    }

    const newValue = parseInt(this.inputTarget.value) || 0

    // No change, just close
    if (newValue === this.originalValue) {
      this.hideInput()
      return
    }

    // Show saving state
    this.element.classList.add('opacity-50')

    try {
      const response = await fetch('/assigned_works/update_project_hours', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          project_id: this.projectIdValue,
          required_hours: newValue
        })
      })

      const data = await response.json()

      if (data.success) {
        this.originalValue = newValue
        this.requiredTarget.textContent = data.required_hours
        this.assignedTarget.textContent = data.total_assigned
        this.hideInput()
        this.showSuccess()
        this.updateColorClass(data.status)

        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('matrix:project-updated', {
          detail: data
        }))
      } else {
        this.showError(data.errors ? data.errors.join(', ') : 'Failed to save')
      }
    } catch (error) {
      this.showError('Network error. Please try again.')
      console.error(error)
    } finally {
      this.element.classList.remove('opacity-50')
    }
  }

  updateColorClass(status) {
    // Remove all allocation color classes
    const colorClasses = ['text-green-600', 'text-yellow-600', 'text-red-600', 'text-gray-500',
                          'bg-green-100', 'bg-yellow-100', 'bg-red-100', 'bg-gray-100']

    this.displayTarget.classList.remove(...colorClasses)

    // Add appropriate color based on status
    switch(status) {
      case 'balanced':
        this.displayTarget.classList.add('text-green-600', 'bg-green-100')
        break
      case 'under_allocated':
        this.displayTarget.classList.add('text-yellow-600', 'bg-yellow-100')
        break
      case 'over_allocated':
        this.displayTarget.classList.add('text-red-600', 'bg-red-100')
        break
      default:
        this.displayTarget.classList.add('text-gray-500')
    }
  }

  showSuccess() {
    this.element.classList.add('ring-2', 'ring-green-500')
    setTimeout(() => {
      this.element.classList.remove('ring-2', 'ring-green-500')
    }, 1000)
  }

  showError(message) {
    this.element.classList.add('ring-2', 'ring-red-500')

    // Show toast notification
    window.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message, type: 'error' }
    }))

    setTimeout(() => {
      this.element.classList.remove('ring-2', 'ring-red-500')
    }, 2000)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }
}
